import { useEffect, useRef, useState } from "react";
import type { JobEvent } from "../../client";

type LogEntry = {
  timestamp: string;
  message: string;
  level: string;
  type: string;
  sectionId?: string;
};

type LiveLogViewerProps = {
  logs: LogEntry[];
  maxLogs?: number;
  autoScroll?: boolean;
  showTimestamps?: boolean;
  filterLevel?: string | null;
};

export function LiveLogViewer({
  logs,
  maxLogs = 500,
  autoScroll = true,
  showTimestamps = true,
  filterLevel = null,
}: LiveLogViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [filter, setFilter] = useState<string>("");
  const [levelFilter, setLevelFilter] = useState<string | null>(filterLevel);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && isAtBottom && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [logs, autoScroll, isAtBottom]);

  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    // Consider "at bottom" if within 50px of bottom
    setIsAtBottom(scrollHeight - scrollTop - clientHeight < 50);
  };

  // Filter logs
  const filteredLogs = logs
    .slice(-maxLogs)
    .filter((log) => {
      if (levelFilter && log.level !== levelFilter) return false;
      if (filter && !log.message.toLowerCase().includes(filter.toLowerCase())) return false;
      return true;
    });

  const getLevelColor = (level: string): string => {
    switch (level) {
      case "error":
        return "#ef5350";
      case "warning":
        return "#ffa726";
      case "success":
        return "#66bb6a";
      case "debug":
        return "#9e9e9e";
      default:
        return "#e0e0e0";
    }
  };

  const getTypeIcon = (type: string): string => {
    switch (type) {
      case "section_start":
        return "▶";
      case "section_complete":
        return "✓";
      case "section_error":
        return "✗";
      case "progress":
        return "⟳";
      case "status":
        return "●";
      default:
        return "│";
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", color: "#e0e0e0" }}>
      {/* Header with filters */}
      <div
        style={{
          padding: "0.5rem",
          borderBottom: "1px solid #333",
          display: "flex",
          gap: "0.5rem",
          alignItems: "center",
          backgroundColor: "#1a1a1a",
        }}
      >
        <input
          type="text"
          placeholder="Filter logs..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{
            flex: 1,
            padding: "0.375rem 0.5rem",
            backgroundColor: "#0d0d0d",
            border: "1px solid #333",
            borderRadius: "4px",
            color: "#e0e0e0",
            fontSize: "0.75rem",
          }}
        />
        <select
          value={levelFilter || ""}
          onChange={(e) => setLevelFilter(e.target.value || null)}
          style={{
            padding: "0.375rem",
            backgroundColor: "#0d0d0d",
            border: "1px solid #333",
            borderRadius: "4px",
            color: "#e0e0e0",
            fontSize: "0.75rem",
          }}
        >
          <option value="">All Levels</option>
          <option value="info">Info</option>
          <option value="success">Success</option>
          <option value="warning">Warning</option>
          <option value="error">Error</option>
          <option value="debug">Debug</option>
        </select>
        <span style={{ fontSize: "0.7rem", color: "#888" }}>
          {filteredLogs.length} / {logs.length}
        </span>
      </div>

      {/* Log content */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        style={{
          flex: 1,
          overflow: "auto",
          backgroundColor: "#0a0a0a",
          fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
          fontSize: "0.75rem",
          lineHeight: 1.5,
        }}
      >
        {filteredLogs.length === 0 ? (
          <div style={{ padding: "2rem", textAlign: "center", color: "#666" }}>
            {logs.length === 0 ? "No logs yet..." : "No matching logs"}
          </div>
        ) : (
          filteredLogs.map((log, idx) => (
            <div
              key={idx}
              style={{
                padding: "0.25rem 0.5rem",
                borderBottom: "1px solid #1a1a1a",
                display: "flex",
                gap: "0.5rem",
                alignItems: "flex-start",
              }}
            >
              {/* Type icon */}
              <span style={{ color: getLevelColor(log.level), width: "1rem", flexShrink: 0 }}>
                {getTypeIcon(log.type)}
              </span>

              {/* Timestamp */}
              {showTimestamps && (
                <span style={{ color: "#666", flexShrink: 0, width: "5rem" }}>
                  {new Date(log.timestamp).toLocaleTimeString()}
                </span>
              )}

              {/* Section badge */}
              {log.sectionId && (
                <span
                  style={{
                    padding: "0 0.25rem",
                    backgroundColor: "#1a237e",
                    borderRadius: "2px",
                    fontSize: "0.65rem",
                    color: "#9fa8da",
                    flexShrink: 0,
                  }}
                >
                  S{log.sectionId}
                </span>
              )}

              {/* Message */}
              <span style={{ color: getLevelColor(log.level), wordBreak: "break-word" }}>
                {log.message}
              </span>
            </div>
          ))
        )}
      </div>

      {/* Scroll indicator */}
      {!isAtBottom && (
        <button
          onClick={() => {
            if (containerRef.current) {
              containerRef.current.scrollTop = containerRef.current.scrollHeight;
              setIsAtBottom(true);
            }
          }}
          style={{
            position: "absolute",
            bottom: "1rem",
            right: "1rem",
            padding: "0.5rem 0.75rem",
            backgroundColor: "#1976d2",
            border: "none",
            borderRadius: "4px",
            color: "#fff",
            fontSize: "0.75rem",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
          }}
        >
          ↓ New logs
        </button>
      )}
    </div>
  );
}

// Helper to convert JobEvent to LogEntry
export function eventToLogEntry(event: JobEvent): LogEntry {
  return {
    timestamp: event.timestamp,
    message: event.message,
    level: event.level,
    type: event.type,
    sectionId: event.section_id || undefined,
  };
}
